import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useCurrentUser, useNavigationHelper } from 'FrontRoyalAngular';
import { faCommentDots } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBoundary } from 'LoadingBoundary';
import { type ErrorHandlingComponentParams } from 'FrontRoyalErrorBoundary';
import { useMessagingConfig } from '../utils/useMessagingConfig';
import { MessagingProvider } from './MessagingProvider';
import { UnreadCount } from './UnreadCount';
import { ErrorMessage } from './ErrorMessage';
import { HandleDisconnection } from './HandleDisconnection';
import { ChannelListeners } from './ChannelListeners';

const classes = {
    container: clsx(
        'absolute',
        'max-sm:ltr:right-[86px]',
        'max-sm:rtl:right-[65px]',
        'ltr:sm:right-[86px]',
        'rtl:sm:left-[86px]',
        'rtl:sm:right-auto',
        'top-1/2',
        '-translate-y-1/2',
        'w-[25px]',
        'h-[25px]',
        'sm:w-[37px]',
        'sm:h-[37px]',
        'md:w-[125px]',
        'max-sm:bg-coral-dark/[0.6] valar:max-sm:bg-blue-dark/[0.6]',
        'sm:border-solid',
        'sm:border',
        'sm:border-slate-grey-200',
        'sm:hover:border-slate-grey-300',
        'sm:bg-white',
        'rounded-2',
        'sm:rounded-full',
        'md:rounded-9',
        'p-[5px]',
        'sm:p-[10px]',
        'md:p-[11px_15px_9px_15px]',
        'z-[101]',
        'cursor-pointer',
    ),
    content: clsx('h-full', 'flex', 'items-center', 'text-white', 'sm:text-slate-grey'),
    text: clsx('hidden', 'md:block', 'ms-[7px]'),
};

const MessagesButtonErrorComponent = (props: ErrorHandlingComponentParams) => <ErrorMessage show={false} {...props} />;

export const MessagesButton = () => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'messaging' });
    const user = useCurrentUser();
    const { appId, accessToken, userId } = useMessagingConfig();
    const { loadRoute } = useNavigationHelper();
    const [connected, setConnected] = useState(true);

    if (!user?.messagingEnabled || !appId || !accessToken || !userId) return null;

    return (
        <LoadingBoundary errorActions={{ other: MessagesButtonErrorComponent }}>
            <button type="button" className={classes.container} onClick={() => loadRoute('/student-network/messages')}>
                <div className={classes.content}>
                    <FontAwesomeIcon icon={faCommentDots} className="text-white sm:text-slate-grey" />
                    <p className={classes.text}>{t('messaging.messages')}</p>
                </div>
            </button>
            <MessagingProvider surface="widget">
                <ChannelListeners />
                <HandleDisconnection setConnected={setConnected} connected={connected} />
                <UnreadCount />
            </MessagingProvider>
        </LoadingBoundary>
    );
};
